import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Copy, Search, Layers, Package as PackageIcon, Coins, Ticket, Pause, Play } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { copyText } from "@/lib/clipboard";
import { PaginationControls } from "@/components/PaginationControls";
import { SharedTemplatePicker } from "./SharedTemplatePicker";

const TYPE_LABEL = {
  credits: { label: "Credits Grant", icon: Coins, color: "text-amber-300" },
  card: { label: "Card Unlock", icon: Layers, color: "text-cyan-300" },
  pack: { label: "Pack Unlock", icon: PackageIcon, color: "text-violet-300" },
  edition: { label: "Edition Claim", icon: Ticket, color: "text-emerald-300" },
  shared_card: { label: "Shared Digital Card", icon: Ticket, color: "text-emerald-300" },
};

export default function AdminCodes() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [templateFilter, setTemplateFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [form, setForm] = useState({ code: "", type: "credits", value: 500, template_id: "", pack_id: "", token_type: "talentshow_free_entry", token_qty: 1, max_uses: 1 });

  const { data: codesResp } = useQuery({
    queryKey: ["admin-codes", search, typeFilter, templateFilter, page, pageSize],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (search) p.set("q", search);
      if (typeFilter !== "all") p.set("type", typeFilter);
      if (templateFilter !== "all") p.set("template_id", templateFilter);
      p.set("page", page);
      p.set("page_size", pageSize);
      return (await api.get(`/admin/codes?${p}`)).data;
    },
  });
  const codes = codesResp?.items || [];
  const total = codesResp?.total || 0;
  const { data: templates } = useQuery({
    queryKey: ["admin-templates", "all"],
    queryFn: async () => (await api.get("/admin/templates?page_size=1000")).data.items,
  });
  const { data: packs } = useQuery({
    queryKey: ["admin-packs"],
    queryFn: async () => (await api.get("/admin/packs")).data,
  });

  const create = useMutation({
    mutationFn: async () => {
      const payload = {
        code: form.code || undefined,
        type: form.type,
        max_uses: form.max_uses,
      };
      if (form.type === "credits") payload.value = form.value;
      if (["card", "shared_card"].includes(form.type)) payload.template_id = form.template_id;
      if (form.type === "pack") payload.pack_id = form.pack_id;
      if (form.type === "token") { payload.token_type = form.token_type; payload.token_qty = form.token_qty; }
      return (await api.post("/admin/codes", payload)).data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["admin-codes"] });
      qc.invalidateQueries({ queryKey: ["admin-templates"] });
      setOpen(false);
      setForm({ code: "", type: "credits", value: 500, template_id: "", pack_id: "", token_type: "talentshow_free_entry", token_qty: 1, max_uses: 1 });
      toast.success(`Code generated: ${data.code}`);
    },
    onError: (e) => toast.error(apiError(e, "Failed")),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/codes/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-codes"] }); toast.success("Deleted"); },
  });

  const canSubmit =
    !create.isPending &&
    (form.type === "credits" ? form.value > 0
      : ["card", "shared_card"].includes(form.type) ? !!form.template_id
      : form.type === "pack" ? !!form.pack_id
      : form.type === "token" ? (!!form.token_type && form.token_qty >= 1)
      : false) &&
    (form.type === "shared_card" || form.max_uses >= 1);

  const toggleShared = useMutation({
    mutationFn: async (code) => (await api.patch(`/admin/shared-codes/${code.id}`, { disabled: !code.disabled })).data,
    onSuccess: (code) => { qc.invalidateQueries({ queryKey: ["admin-codes"] }); toast.success(code.disabled ? "Shared code disabled — claim history preserved" : "Shared code enabled"); },
    onError: (e) => toast.error(apiError(e, "Could not update code")),
  });

  const renderTarget = (c) => {
    if (c.type === "credits") return `${c.value?.toLocaleString()} credits`;
    if (c.type === "edition") {
      return `${c.card_title || "Card"} · #${c.edition_number}`;
    }
    if (["card", "shared_card"].includes(c.type)) {
      if (c.card_title) return c.card_title;
      const t = (templates || []).find((tp) => tp.id === c.template_id);
      return c.card_title || (t ? `${t.title} · ${t.rarity}` : "Card (missing)");
    }
    if (c.type === "pack") {
      const p = (packs || []).find((pp) => pp.id === c.pack_id);
      return p ? `${p.name} (random)` : "Pack (missing)";
    }
    if (c.type === "token") {
      const names = { talentshow_free_entry: "Talent Show Free Entry", hq_enhancement: "HQ Enhancement Token" };
      return `${c.token_qty || 1}× ${names[c.token_type] || c.token_type}`;
    }
    return "—";
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-3xl text-white tracking-[0.06em]">CLAIM CODES</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="admin-new-code-button" className="bg-cyan-400 text-black hover:bg-cyan-300"><Plus className="h-4 w-4 mr-1" /> New Code</Button></DialogTrigger>
          <DialogContent data-testid="new-code-dialog" className="bg-[hsl(222_22%_8%)] border-white/10 w-[calc(100%-2rem)] max-w-md max-h-[90dvh] overflow-y-auto rounded-lg">
            <DialogHeader><DialogTitle className="font-display text-2xl tracking-[0.08em]">NEW CODE</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Code (optional — auto-generated)</Label>
                <Input data-testid="code-string-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="WELCOME-1000" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10 font-mono uppercase" />
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Type of Code</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, template_id: "", pack_id: "" })}>
                  <SelectTrigger data-testid="code-type-select" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10">
                    <SelectItem value="credits">💰 Credits Grant</SelectItem>
                    <SelectItem value="card">🎟️ Card Unlock (specific card)</SelectItem>
                    <SelectItem value="shared_card" data-testid="code-type-shared-card">Shared Digital Card · once per account</SelectItem>
                    <SelectItem value="pack">🎁 Pack Unlock (random from pack)</SelectItem>
                    <SelectItem value="token">🎟️ Inventory Token</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.type === "credits" && (
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Credits Amount</Label>
                  <Input data-testid="code-value-input" type="number" min={1} value={form.value} onChange={(e) => setForm({ ...form, value: Number(e.target.value) })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
                </div>
              )}

              {form.type === "card" && (
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Card Template</Label>
                  <Select value={form.template_id} onValueChange={(v) => setForm({ ...form, template_id: v })}>
                    <SelectTrigger data-testid="code-template-select" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10">
                      <SelectValue placeholder={(templates || []).length === 0 ? "No templates yet — create one first" : "Select template…"} />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10 max-h-[320px]">
                      {(templates || []).map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.title} · {t.rarity} ({t.minted_count}/{t.edition_count})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-[10px] text-white/40 mt-1">Redemption mints this specific card to the user.</div>
                </div>
              )}

              {form.type === "pack" && (
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Pack</Label>
                  <Select value={form.pack_id} onValueChange={(v) => setForm({ ...form, pack_id: v })}>
                    <SelectTrigger data-testid="code-pack-select" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10">
                      <SelectValue placeholder={(packs || []).length === 0 ? "No packs yet — create one first" : "Select pack…"} />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10 max-h-[320px]">
                      {(packs || []).map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name} · {p.price_credits} credits
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-[10px] text-white/40 mt-1">Redemption opens the pack and mints a random card per its odds.</div>
                </div>
              )}

              {form.type === "shared_card" && <div className="space-y-3">
                <Label data-testid="shared-card-source-label" className="text-white/70 text-xs">Source card design</Label>
                <SharedTemplatePicker value={form.template_id} onChange={(template_id) => setForm({ ...form, template_id })} />
                <p data-testid="shared-code-rules" className="text-xs leading-relaxed text-emerald-200 border-y border-white/10 py-3">Separate digital-only series · Supply grows with claims · One claim per account, ever.</p>
                <p data-testid="shared-code-loss-warning" className="text-xs leading-relaxed text-amber-200">On forfeit, the card stays in its owner’s Playden with all stats intact, permanently locked out. No recovery or physical editions.</p>
              </div>}

              {form.type === "token" && (
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Token Type</Label>
                    <Select value={form.token_type} onValueChange={(v) => setForm({ ...form, token_type: v })}>
                      <SelectTrigger data-testid="code-token-type-select" className="mt-1 bg-[hsl(220_18%_12%)] border-white/10"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10">
                        <SelectItem value="talentshow_free_entry">🎟️ Talent Show Free Entry</SelectItem>
                        <SelectItem value="hq_enhancement">✨ HQ Enhancement Token</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Quantity per redemption</Label>
                    <Input data-testid="code-token-qty-input" type="number" min={1} max={999} value={form.token_qty} onChange={(e) => setForm({ ...form, token_qty: Number(e.target.value) })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
                    <div className="text-[10px] text-white/40 mt-1">Redemption adds these tokens to the player's inventory.</div>
                  </div>
                </div>
              )}

              {form.type !== "shared_card" && <div>
                <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Max Uses</Label>
                <Input data-testid="code-max-uses-input" type="number" min={1} value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: Number(e.target.value) })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
              </div>}
              <Button data-testid="code-create-button" onClick={() => create.mutate()} disabled={!canSubmit} className="w-full bg-cyan-400 text-black hover:bg-cyan-300 disabled:opacity-50">{create.isPending ? "Creating…" : "Generate Code"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters: stack on narrow, side-by-side on wide. Never overlap. */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 basis-[260px] min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input data-testid="admin-codes-search" placeholder="Search by code or card name…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} className="pl-9 bg-[hsl(220_18%_12%)] border-white/10 font-mono" />
        </div>
        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(0); }}>
          <SelectTrigger className="w-44 bg-[hsl(220_18%_12%)] border-white/10" data-testid="admin-codes-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10">
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="credits">Credits</SelectItem>
            <SelectItem value="card">Card</SelectItem>
            <SelectItem value="pack">Pack</SelectItem>
            <SelectItem value="edition">Edition Claim</SelectItem>
            <SelectItem value="shared_card" data-testid="code-filter-shared-card">Shared Digital Card</SelectItem>
          </SelectContent>
        </Select>
        <Select value={templateFilter} onValueChange={(v) => { setTemplateFilter(v); setPage(0); }}>
          <SelectTrigger className="w-56 bg-[hsl(220_18%_12%)] border-white/10" data-testid="admin-codes-card-filter"><SelectValue placeholder="Filter by card" /></SelectTrigger>
          <SelectContent className="bg-[hsl(220_18%_10%)] border-white/10 max-h-[320px]">
            <SelectItem value="all">All Cards</SelectItem>
            {(templates || []).map((t) => (
              <SelectItem key={t.id} value={t.id}>{t.title} · {t.rarity}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="hud-pill rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-white/5 text-left font-mono text-[10px] uppercase tracking-[0.25em] text-white/50">
              <tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Target</th><th className="px-4 py-3">Uses</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {(codes || []).map((c) => {
                const meta = TYPE_LABEL[c.type] || TYPE_LABEL.credits;
                const Icon = meta.icon;
                return (
                  <tr key={c.id} className="border-t border-white/5">
                    <td className="px-4 py-3 font-mono text-white">
                      <button onClick={async () => { (await copyText(c.code)) ? toast.success("Copied") : toast.error("Couldn't copy"); }} className="inline-flex items-center gap-1.5 hover:text-cyan-300" data-testid={`copy-code-${c.id}`}>
                        {c.code} <Copy className="h-3 w-3 text-white/40" />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] ${meta.color}`}>
                        <Icon className="h-3.5 w-3.5" /> {c.type === "shared_card" ? "Shared digital" : c.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white/80 truncate max-w-[260px]">{renderTarget(c)}</td>
                    <td data-testid={`code-usage-${c.id}`} className="px-4 py-3 font-mono text-white/70">{c.type === "shared_card" ? `${c.redeemed_count || 0} / ∞` : `${c.used_by?.length || 0} / ${c.max_uses}`}{c.type === "shared_card" && <span data-testid={`code-status-${c.id}`} className="block text-[10px] text-white/45">{c.disabled ? "Disabled" : "Active"} · once/account</span>}</td>
                    <td className="px-4 py-3 text-right">
                      {c.type === "shared_card" ? <Button data-testid={`toggle-shared-code-${c.id}`} variant="ghost" size="icon" disabled={toggleShared.isPending} title={c.disabled ? "Enable shared code" : "Disable shared code"} aria-label={c.disabled ? "Enable shared code" : "Disable shared code"} onClick={() => toggleShared.mutate(c)} className="text-emerald-300">{c.disabled ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}</Button> : <Button data-testid={`delete-code-${c.id}`} variant="ghost" size="sm" onClick={() => { if (window.confirm("Delete code?")) del.mutate(c.id); }} className="text-red-400"><Trash2 className="h-4 w-4" /></Button>}
                    </td>
                  </tr>
                );
              })}
              {(codes || []).length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-white/40">No codes match filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <PaginationControls
        page={page}
        total={total}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
        testidPrefix="admin-codes-pagination"
      />
    </div>
  );
}
